import type {
  AccommodationInput,
  AccommodationSummary,
  BudgetInput,
  BudgetOverview,
  BudgetSettings,
  ChecklistItemSummary,
  CommentSummary,
  CreateExpenseInput,
  CreateItineraryItemInput,
  CreateInviteInput,
  CreateTripInput,
  ErrorCode,
  ExpenseSummary,
  FlightInput,
  FlightSummary,
  InviteSummary,
  MemberSummary,
  PlaceSummary,
  ProposalSummary,
  PublicUser,
  ReservationInput,
  SettlementEntry,
  TodayOverview,
  TripDayDetail,
  TripDetail,
  TripSummary,
  UpdateItineraryItemInput,
  UpdateTripInput,
  WishlistEntry,
} from '@tappa/shared';

/**
 * Client HTTP unico.
 *
 * Nessun componente chiama `fetch` direttamente: gestione errori, cookie e
 * comportamento offline stanno in un posto solo.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | 'network_error';
  readonly details: unknown;

  constructor(status: number, code: ApiError['code'], message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** true se il problema e' la connessione, non la richiesta. */
  get isOffline(): boolean {
    return this.code === 'network_error';
  }
}

interface ErrorPayload {
  error?: { code?: string; message?: string; details?: unknown };
}

async function request<T>(path: string, init: RequestInit & { method: string }): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      ...init,
      // Il cookie di sessione e' HttpOnly: serve `credentials: 'include'`.
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });
  } catch {
    throw new ApiError(
      0,
      'network_error',
      'Non riesco a raggiungere il server. Controlla la connessione.',
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed: unknown = text === '' ? null : JSON.parse(text);

  if (!response.ok) {
    const payload = (parsed ?? {}) as ErrorPayload;
    throw new ApiError(
      response.status,
      (payload.error?.code as ErrorCode | undefined) ?? 'internal',
      payload.error?.message ?? 'Richiesta non riuscita',
      payload.error?.details,
    );
  }

  return parsed as T;
}

const get = <T,>(path: string): Promise<T> => request<T>(path, { method: 'GET' });
const post = <T,>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
const put = <T,>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
const patch = <T,>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const del = <T,>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' });

export interface MeteoGiorno {
  readonly date: string;
  readonly tempMinC: number | null;
  readonly tempMaxC: number | null;
  readonly rainChance: number | null;
  readonly code: number | null;
  readonly label: string;
  readonly icon: string;
}

export interface Capabilities {
  readonly appName: string;
  readonly providers: Record<string, string>;
  readonly features: {
    readonly routingIsEstimate: boolean;
    readonly transitAvailable: boolean;
    readonly weatherAvailable: boolean;
    readonly flightRealtime: boolean;
    /** Falso quando i voti sono spenti: il filtro per stelle resta nascosto. */
    readonly ratingsAvailable: boolean;
    /** Falso senza chiavi VAPID: il pannello notifiche non compare. */
    readonly pushAvailable: boolean;
  };
}

export interface PlaceSearchQuery extends Record<string, unknown> {
  section?: string;
  category?: string;
  q?: string;
  nearLat?: number;
  nearLon?: number;
  radiusM?: number;
  freeOnly?: boolean;
  maxPriceTier?: string;
  minRating?: number;
  minRatingsCount?: number;
  limit?: number;
}

function toQueryString(query: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded === '' ? '' : `?${encoded}`;
}

export const api = {
  capabilities: (): Promise<Capabilities> => get('/api/capabilities'),

  push: {
    key: (): Promise<{ available: boolean; publicKey: string | null }> => get('/api/push/key'),
    subscribe: (iscrizione: {
      endpoint: string;
      keys: { p256dh: string; auth: string };
    }): Promise<{ ok: boolean }> => post('/api/push/subscribe', iscrizione),
    unsubscribe: (endpoint: string): Promise<void> =>
      post('/api/push/unsubscribe', { endpoint }),
  },

  /** Previsioni giorno per giorno. Mappa `AAAA-MM-GG` → meteo. */
  weather: (
    tripId: string,
  ): Promise<{
    days: Record<string, MeteoGiorno>;
    forecastDays: number;
    available: boolean;
  }> => get(`/api/trips/${tripId}/weather`),

  auth: {
    register: (input: { email: string; password: string; displayName: string }): Promise<{ user: PublicUser }> =>
      post('/api/auth/register', input),
    login: (input: { email: string; password: string }): Promise<{ user: PublicUser }> =>
      post('/api/auth/login', input),
    logout: (): Promise<void> => post<void>('/api/auth/logout'),
    me: (): Promise<{ user: PublicUser }> => get('/api/auth/me'),
  },

  trips: {
    list: (): Promise<{ trips: TripSummary[] }> => get('/api/trips'),
    create: (input: CreateTripInput): Promise<{ trip: TripDetail }> => post('/api/trips', input),
    detail: (tripId: string): Promise<{ trip: TripDetail }> => get(`/api/trips/${tripId}`),
    update: (tripId: string, input: UpdateTripInput): Promise<{ trip: TripDetail }> =>
      patch(`/api/trips/${tripId}`, input),
    remove: (tripId: string): Promise<void> => del<void>(`/api/trips/${tripId}`),
    addAccommodation: (
      tripId: string,
      input: AccommodationInput,
    ): Promise<{ accommodation: AccommodationSummary }> =>
      post(`/api/trips/${tripId}/accommodations`, input),
    today: (tripId: string): Promise<{ overview: TodayOverview }> => get(`/api/trips/${tripId}/today`),
  },

  accommodations: {
    list: (tripId: string): Promise<{ accommodations: AccommodationSummary[] }> =>
      get(`/api/trips/${tripId}/accommodations`),
    /**
     * `located` dice se l'indirizzo e' finito su un punto della mappa. Senza,
     * l'alloggio si salva lo stesso ma non puo' fare da inizio e fine giornata:
     * e' un'informazione che va data subito, non scoperta dopo.
     */
    create: (
      tripId: string,
      input: Partial<AccommodationInput> & { name: string },
    ): Promise<{ accommodation: AccommodationSummary; located: boolean }> =>
      post(`/api/trips/${tripId}/accommodations`, input),
    update: (
      tripId: string,
      id: string,
      input: Partial<AccommodationInput>,
    ): Promise<{ accommodation: AccommodationSummary; located: boolean }> =>
      patch(`/api/trips/${tripId}/accommodations/${id}`, input),
    remove: (tripId: string, id: string): Promise<void> =>
      del<void>(`/api/trips/${tripId}/accommodations/${id}`),
  },

  days: {
    detail: (tripId: string, dayId: string): Promise<{ day: TripDayDetail }> =>
      get(`/api/trips/${tripId}/days/${dayId}`),
    /** Risolve un indirizzo o un link di Google Maps in coordinate. */
    resolvePlace: (
      tripId: string,
      dayId: string,
      query: string,
    ): Promise<{
      place: { lat: number; lon: number; label: string };
      nearestDistanceM: number | null;
      isFar: boolean;
      message: string | null;
    }> => post(`/api/trips/${tripId}/days/${dayId}/resolve-place`, { query }),
    update: (
      tripId: string,
      dayId: string,
      input: { dayStartLocal?: string; dayEndLocal?: string; dailyBudgetMinor?: number | null },
    ): Promise<{ day: TripDayDetail }> => patch(`/api/trips/${tripId}/days/${dayId}`, input),
    generate: (
      tripId: string,
      dayId: string,
      replaceExisting = true,
    ): Promise<{
      day: TripDayDetail;
      added: number;
      rejected: Array<{ name: string; reason: string }>;
      message: string;
    }> => post(`/api/trips/${tripId}/days/${dayId}/generate`, { replaceExisting }),
  },

  items: {
    create: (
      tripId: string,
      input: CreateItineraryItemInput,
    ): Promise<{ itemId: string; day: TripDayDetail }> => post(`/api/trips/${tripId}/items`, input),
    update: (
      tripId: string,
      itemId: string,
      input: UpdateItineraryItemInput,
    ): Promise<{ day: TripDayDetail }> => patch(`/api/trips/${tripId}/items/${itemId}`, input),
    remove: (tripId: string, itemId: string): Promise<{ day: TripDayDetail }> =>
      del(`/api/trips/${tripId}/items/${itemId}`),
    reorder: (
      tripId: string,
      tripDayId: string,
      itemIds: string[],
    ): Promise<{ day: TripDayDetail }> =>
      post(`/api/trips/${tripId}/items/reorder`, { tripDayId, itemIds }),
  },

  places: {
    search: (
      tripId: string,
      query: PlaceSearchQuery,
    ): Promise<{
      places: PlaceSummary[];
      destination: { id: string; name: string; poiIngestedAt: string | null };
      needsImport: boolean;
  /** Vero quando il GPS era troppo lontano ed e' stato ignorato. */
  positionIgnored?: boolean;
    }> => get(`/api/trips/${tripId}/places${toQueryString(query)}`),
    detail: (tripId: string, placeId: string): Promise<{ place: PlaceSummary }> =>
      get(`/api/trips/${tripId}/places/${placeId}`),
    import: (
      tripId: string,
      force = false,
    ): Promise<{ status: string; imported: number; total: number; message: string }> =>
      post(`/api/trips/${tripId}/places/import`, { force }),
  },

  budget: {
    get: (
      tripId: string,
    ): Promise<{ budget: BudgetSettings; overview: BudgetOverview }> =>
      get(`/api/trips/${tripId}/budget`),
    set: (
      tripId: string,
      input: BudgetInput,
    ): Promise<{ budget: BudgetSettings; overview: BudgetOverview }> =>
      put(`/api/trips/${tripId}/budget`, input),
  },

  expenses: {
    list: (tripId: string): Promise<{ expenses: ExpenseSummary[]; settlement: SettlementEntry[] }> =>
      get(`/api/trips/${tripId}/expenses`),
    create: (
      tripId: string,
      input: CreateExpenseInput,
    ): Promise<{
      expenses: ExpenseSummary[];
      overview: BudgetOverview;
      settlement: SettlementEntry[];
    }> => post(`/api/trips/${tripId}/expenses`, input),
    remove: (tripId: string, id: string): Promise<void> =>
      del<void>(`/api/trips/${tripId}/expenses/${id}`),
  },

  flights: {
    list: (
      tripId: string,
    ): Promise<{
      flights: FlightSummary[];
      capabilities: { realtime: boolean; gate: boolean; delay: boolean; source: string };
    }> => get(`/api/trips/${tripId}/flights`),
    create: (
      tripId: string,
      input: Partial<FlightInput> & { depAirport: string; arrAirport: string; depScheduled: Date },
    ): Promise<{ flight: FlightSummary }> => post(`/api/trips/${tripId}/flights`, input),
    update: (
      tripId: string,
      id: string,
      input: Partial<FlightInput>,
    ): Promise<{ flight: FlightSummary }> => patch(`/api/trips/${tripId}/flights/${id}`, input),
    remove: (tripId: string, id: string): Promise<void> =>
      del<void>(`/api/trips/${tripId}/flights/${id}`),
  },

  reservations: {
    list: (tripId: string): Promise<{ reservations: Array<Record<string, unknown>> }> =>
      get(`/api/trips/${tripId}/reservations`),
    create: (tripId: string, input: ReservationInput): Promise<{ id: string }> =>
      post(`/api/trips/${tripId}/reservations`, input),
    remove: (tripId: string, id: string): Promise<void> =>
      del<void>(`/api/trips/${tripId}/reservations/${id}`),
  },

  checklist: {
    list: (tripId: string): Promise<{ items: ChecklistItemSummary[] }> =>
      get(`/api/trips/${tripId}/checklist`),
    add: (
      tripId: string,
      input: { category: string; label: string },
    ): Promise<{ items: ChecklistItemSummary[] }> => post(`/api/trips/${tripId}/checklist`, input),
    setState: (
      tripId: string,
      id: string,
      state: 'todo' | 'done' | 'missing' | 'na',
    ): Promise<{ items: ChecklistItemSummary[] }> =>
      patch(`/api/trips/${tripId}/checklist/${id}`, { state }),
    remove: (tripId: string, id: string): Promise<void> =>
      del<void>(`/api/trips/${tripId}/checklist/${id}`),
  },

  proposals: {
    list: (tripId: string): Promise<{ proposals: ProposalSummary[] }> =>
      get(`/api/trips/${tripId}/proposals`),
    create: (
      tripId: string,
      input: { title: string; placeId?: string | null; note?: string | null },
    ): Promise<{ proposals: ProposalSummary[] }> => post(`/api/trips/${tripId}/proposals`, input),
    vote: (tripId: string, id: string, value: 1 | -1): Promise<{ proposals: ProposalSummary[] }> =>
      post(`/api/trips/${tripId}/proposals/${id}/vote`, { value }),
    resolve: (
      tripId: string,
      id: string,
      status: 'accepted' | 'rejected',
    ): Promise<{
      proposals: ProposalSummary[];
      addedToWishlist: boolean;
      message: string;
    }> => post(`/api/trips/${tripId}/proposals/${id}/resolve`, { status }),
  },

  comments: {
    list: (
      tripId: string,
      subjectType: string,
      subjectId: string,
    ): Promise<{ comments: CommentSummary[] }> =>
      get(`/api/trips/${tripId}/comments${toQueryString({ subjectType, subjectId })}`),
    add: (
      tripId: string,
      subjectType: string,
      subjectId: string,
      body: string,
    ): Promise<{ comments: CommentSummary[] }> =>
      post(`/api/trips/${tripId}/comments`, { subjectType, subjectId, body }),
  },

  members: {
    list: (tripId: string): Promise<{ members: MemberSummary[] }> =>
      get(`/api/trips/${tripId}/members`),
    changeRole: (
      tripId: string,
      userId: string,
      role: 'editor' | 'viewer',
    ): Promise<{ member: MemberSummary }> =>
      patch(`/api/trips/${tripId}/members/${userId}`, { role }),
    remove: (tripId: string, userId: string): Promise<void> =>
      del<void>(`/api/trips/${tripId}/members/${userId}`),
  },

  invites: {
    create: (
      tripId: string,
      input: Partial<CreateInviteInput>,
    ): Promise<{ invite: InviteSummary & { url: string } }> =>
      post(`/api/trips/${tripId}/invites`, input),
    list: (tripId: string): Promise<{ invites: InviteSummary[] }> =>
      get(`/api/trips/${tripId}/invites`),
    revoke: (tripId: string, inviteId: string): Promise<void> =>
      del<void>(`/api/trips/${tripId}/invites/${inviteId}`),
    accept: (token: string): Promise<{ tripId: string; role: string; alreadyMember: boolean }> =>
      post('/api/invites/accept', { token }),
  },
};

/* -------------------------------------------------------------------------- */
/* Lista dei desideri e rilettura dei dati                                     */
/* -------------------------------------------------------------------------- */

export interface FreeDayHint {
  readonly placeId: string;
  readonly name: string;
  readonly label: string;
  readonly verified: boolean;
}

export const wishlistApi = {
  list: (
    tripId: string,
  ): Promise<{ entries: WishlistEntry[]; freeDays: FreeDayHint[] }> =>
    get(`/api/trips/${tripId}/wishlist`),

  add: (
    tripId: string,
    input: { placeId: string; priority?: number; note?: string | null },
  ): Promise<{ entries: WishlistEntry[] }> => post(`/api/trips/${tripId}/wishlist`, input),

  setPriority: (
    tripId: string,
    entryId: string,
    priority: number,
  ): Promise<{ entries: WishlistEntry[] }> =>
    patch(`/api/trips/${tripId}/wishlist/${entryId}`, { priority }),

  remove: (tripId: string, entryId: string): Promise<void> =>
    del<void>(`/api/trips/${tripId}/wishlist/${entryId}`),

  organize: (
    tripId: string,
    replaceExisting = true,
  ): Promise<{
    days: TripDayDetail[];
    placed: number;
    leftOut: Array<{ name: string; reason: string }>;
    message: string;
  }> => post(`/api/trips/${tripId}/wishlist/organize`, { replaceExisting }),
};

export const freshnessApi = {
  refreshPlace: (
    tripId: string,
    placeId: string,
    force = false,
  ): Promise<{ status: string; message: string; checkedAt: string | null }> =>
    post(`/api/trips/${tripId}/places/${placeId}/refresh`, { force }),

  refreshDay: (
    tripId: string,
    dayId: string,
  ): Promise<{ refreshed: number; failed: number; message: string }> =>
    post(`/api/trips/${tripId}/days/${dayId}/refresh-places`),
};
